import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from '@orion-platform/core';
import { Column } from '@orion-platform/datagrid';
import { environment } from '@orion/environment';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import {
  SLOTS_COLUMN_LIST,
  SLOTS_FIELDS_KEY,
} from '../components/slot-list-widget/slot-list-widget.consts';
import { RequestStatus } from '../models/commit.model';
import { GlobalStorage } from '../models/slots-storage.model';
import {
  EnumSlotsFieldStructure,
  SlotsField,
  SlotsFilterDto,
  SlotsFilterUIModel,
} from '../models/slots.model';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import { DataGridResult } from '../store/partnumbers-list/partnumbers-list.types';
import {
  Carrier,
  CreateSlotInput,
  DownloadProps,
  Slot,
  SlotHistoryResponse,
  SlotListLoadOptions,
  SlotsFilterRequest,
  SlotsNavbarProps,
} from '../store/slots/slots.types';
import { generateHttpParams, getUrl } from '../utils/general.utils';
import { split } from '../utils/string.utils';
import { DefaultFormSettingsService } from './default-form-settings.service';
import { FileUploadService } from './file-upload.service';
import { SlotCalendarService } from './slot-calendar.service';
import { SupplyVisibilityService } from './supply-visibility.service';

import { ColumnsState } from '../types/columns.types';
import { ColumnsService } from './columns.service';

@Injectable({
  providedIn: 'root',
})
export class SlotsService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly fileUpload = inject(FileUploadService);
  private readonly defaultForms = inject(DefaultFormSettingsService);

  private readonly columns = inject(ColumnsService);
  private readonly supplyVisibility = inject(SupplyVisibilityService);
  private readonly slotCalendarService = inject(SlotCalendarService);

  private readonly baseUrl = this.config.getSettings('apiBaseUrl') as string;
  private readonly mm = environment.modulesBaseUrl.materialManagement;

  currentSlots: Slot | undefined;
  private slotsDetails = new Subject<Slot | undefined>();
  private isSearchVisible = new Subject<boolean>();

  loadNavbarData = async (): Promise<SlotsNavbarProps> => {
    const carriers = await firstValueFrom(this.getSlotCarriers());
    const storagesResponse = await firstValueFrom(this.slotCalendarService.getSlotStorages());

    const storages: GlobalStorage[] = storagesResponse.map((storage): GlobalStorage => ({
      docks: storage['docks'],
      storage: storage['storage'],
    }));

    return {
      carriers: carriers.map((carrier): Carrier => ({
        name: carrier,
        value: carrier,
      })),
      storages,
    };
  };

  /**
   * Slots table columns. `narrowToAllowed` preserves the previous behaviour of
   * dropping columns the form settings do not list.
   */
  getDefaultColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (): Column[] =>
        new SlotsField(EnumSlotsFieldStructure.SlotsTable).fields as Column[],
      columnListKey: SLOTS_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(SLOTS_FIELDS_KEY),
      narrowToAllowed: true,
    });

  getSlotsByFilter = (request: SlotsFilterRequest): Observable<Slot[]> => {
    const { status, ...rest } = request;
    const url = this.slotsModuleUrl(this.mm.slotsModule.getSlotsByFilter);
    return this.http.post<Slot[]>(url, { ...rest, type: status });
  };

  downloadXlsxSlots = (props: DownloadProps): Observable<Blob> => {
    const url = this.slotsModuleUrl(this.mm.slotsModule.downloadXlsxSlots);
    return this.http.post(url, props, { responseType: 'blob' });
  };

  getSlotCarriers(): Observable<string[]> {
    return this.http.get<string[]>(`${this.slotsUrl}/Carriers`);
  }

  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    return this.defaultForms.getFields(this.slotsUrl, formKey);
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    return this.defaultForms.formKeys(this.slotsUrl);
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    return this.defaultForms.getFormSettings(this.slotsUrl, formKey);
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    return this.defaultForms.update(this.slotsUrl, formKey, payload);
  }

  getStorages(): Observable<GlobalStorage[]> {
    return this.http.get<GlobalStorage[]>(`${this.slotStoragesUrl}/GetStorages`);
  }

  private buildSlotsHttpParams(searchParams?: SlotListLoadOptions): HttpParams {
    if (!searchParams) return new HttpParams();

    return generateHttpParams({
      $orderby: searchParams.sort as string,
      $skip: searchParams.skip,
      $top: searchParams.top,
      searchText: searchParams.searchValue,
    });
  }

  createSlot(slot: CreateSlotInput): Observable<Slot> {
    return this.http.post<Slot>(`${this.slotsUrl}/CreateBlank`, slot);
  }

  getSlotHistory(id: string): Observable<SlotHistoryResponse> {
    return this.http.get<SlotHistoryResponse>(`${this.slotsUrl}/${id}/History`);
  }

  downloadSlots(filter: SlotsFilterUIModel, columns: string[]): Observable<Blob> {
    const dtoFilter: Record<string, unknown> = {
      ...this.getSlotsFilter(filter),
      Fields: columns,
      type: 'XLSX',
    };

    if (filter.sorts) {
      dtoFilter['sorts'] = filter.sorts;
    }

    return this.http.post(`${this.slotsUrl}/GetUploadSlots`, dtoFilter, {
      responseType: 'blob',
    });
  }

  getSlots(
    filter: SlotsFilterUIModel,
    searchParams?: SlotListLoadOptions,
  ): Observable<DataGridResult<Slot>> {
    const dtoFilter = this.getSlotsFilter(filter);
    const params = this.buildSlotsHttpParams(searchParams);
    return this.http.post<DataGridResult<Slot>>(this.slotsUrl, dtoFilter, { params });
  }

  private getSlotsFilter(filter: SlotsFilterUIModel): SlotsFilterDto {
    return {
      codes: split(filter.slotCodes),
      docks: split(filter.docks),
      shipmentNumbers: split(filter.shipmentNumbers),
      slotDateFrom: filter.slotDateFrom,
      slotDateTo: filter.slotDateTo,
      storages: split(filter.storages),
    };
  }

  uploadSlots(fileName: string, file: File): Observable<HttpEvent<RequestStatus>> {
    const url = `${this.slotsUrl}/PostUploadSlots`;
    return this.fileUpload.uploadFormData(url, fileName, file);
  }

  batchEditSlotsRequestStatus(requestHash: string): Observable<RequestStatus> {
    return this.http.get<RequestStatus>(`${this.slotsUrl}/-/RequestStatus/${requestHash}`);
  }

  private get slotsUrl(): string {
    return getUrl(this.baseUrl, this.mm.slots);
  }

  private get slotStoragesUrl(): string {
    return getUrl(this.baseUrl, this.mm.slotStorages);
  }

  private slotsModuleUrl(route: string): string {
    return getUrl(this.baseUrl, route);
  }

  getSlotsListReady(): Observable<boolean> {
    return this.isSearchVisible.asObservable();
  }

  setSlotsListReady(data: boolean): void {
    this.isSearchVisible.next(data);
  }

  getSlotsDetail(): Observable<Slot | undefined> {
    return this.slotsDetails.asObservable();
  }

  setSlotsDetail(data?: Slot): void {
    this.currentSlots = data;
    this.slotsDetails.next(data);
  }
}
